import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router';
import {
   AdminActivateBlogDto,
   AdminActivateBlogResponse,
   AdminBlogIndexResponse,
   AdminDeactivateBlogDto,
   AdminDeactivateBlogResponse,
   AdminDeleteBlogDto,
   AdminDeleteBlogResponse,
   ManageBlog,
} from '../types';
import { AsyncState } from 'shared/libs/state';
import { BlogsManagerApiService } from '../BlogsManagerApiService';
import Cookies from 'js-cookie';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { PaginationControl } from 'shared/components/pagination';

type SortDirection = 'asc' | 'desc';
type BlogSort = keyof Pick<ManageBlog, 'title' | 'created'>;

export class BlogsManagerStore {
   readonly loadBlogsState = new AsyncState<AdminBlogIndexResponse>();
   readonly activateBlogState = new AsyncState<AdminActivateBlogResponse>();
   readonly deactivateBlogState = new AsyncState<AdminDeactivateBlogResponse>();
   readonly deleteBlogState = new AsyncState<AdminDeleteBlogResponse>();

   readonly activateAllBlogState = new AsyncState<AdminBlogIndexResponse>();
   readonly deactivateAllBlogState = new AsyncState<AdminBlogIndexResponse>();
   readonly deleteAllBlogState = new AsyncState<AdminBlogIndexResponse>();

   rawBlogs: ManageBlog[] = [];
   idsArray: number[] = [];
   searchStr: string = '';
   sorter: BlogSort = 'created';
   direction: SortDirection = 'desc';
   toPage = '1';

   private readonly PER_PAGE = 20;
   readonly pagination = new PaginationControl();

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawBlogs: observable,
         searchStr: observable,
         sorter: observable,
         direction: observable,
         idsArray: observable,
         toPage: observable,
         onSelectAll: action.bound,
         onDeselectAll: action.bound,
         onToggleById: action.bound,
         onSearchChange: action.bound,
         onToPageChange: action.bound,
         goToPage: action.bound,
         onSortBy: action.bound,
         totalPages: computed,
         filteredBlogs: computed,
         blogs: computed,
         selectedAll: computed,
         disableActivateAll: computed,
      });
   }

   get filteredBlogs(): ManageBlog[] {
      const search = this.searchStr.trim().toLocaleLowerCase();
      const filteredBlogs = this.rawBlogs.filter((blog) => {
         const matchesSearch =
            !search ||
            blog.title?.toLocaleLowerCase().includes(search) ||
            blog.created.toLocaleLowerCase().includes(search);

         return matchesSearch;
      });

      return filteredBlogs.sort(this._getSortCallback());
   }

   get blogs(): ManageBlog[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.filteredBlogs.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get totalPages(): number {
      return Math.ceil(this.filteredBlogs.length / this.PER_PAGE);
   }

   get selectedAll(): boolean {
      return this.rawBlogs.length !== 0 && this.idsArray.length === this.rawBlogs.length;
   }

   get disableActivateAll(): boolean {
      return this.idsArray.length === 0;
   }

   get diasableDeactivateAll(): boolean {
      return this.idsArray.length === 0;
   }

   onSearchChange(value: string): void {
      this.searchStr = value;
      this.pagination.setPage(0);
   }

   onSelectAll(): void {
      this.idsArray = this.rawBlogs.map((b) => b.id);
   }

   onDeselectAll(): void {
      this.idsArray = [];
   }

   onToggleById(id: number) {
      if (this.idsArray.includes(id)) {
         this.idsArray = this.idsArray.filter((planId) => planId !== id);
      } else {
         this.idsArray = [...this.idsArray, id];
      }
   }

   onSortBy(sort: BlogSort): void {
      if (this.sorter === sort) {
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      } else {
         this.sorter = sort;
         if (this.direction === 'asc') {
            this.direction = 'desc';
         } else {
            this.direction = 'asc';
         }
      }
   }

   onToPageChange(value: string): void {
      const regExp = /^\d+$/;
      if (regExp.test(value) || value === '') {
         this.toPage = value;
      }
   }

   goToPage(): void {
      if (this.toPage === '') {
         this.toPage = '1';
      }
      const page = Number(this.toPage);
      if (!Number.isNaN(page)) {
         this.pagination.setPage(page - 1);
      }
   }

   private _getSortCallback() {
      if (this.sorter === 'created') {
         return (a: ManageBlog, b: ManageBlog) => {
            if (this.direction === 'asc') {
               return Number(a.created) - Number(b.created);
            } else {
               return Number(b.created) - Number(a.created);
            }
         };
      }
      return (a: ManageBlog, b: ManageBlog) => {
         if (a[this.sorter] < b[this.sorter]) {
            return this.direction === 'asc' ? -1 : 1;
         }
         if (a[this.sorter] > b[this.sorter]) {
            return this.direction === 'asc' ? 1 : -1;
         }
         return 0;
      };
   }

   async loadBlogs(): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.loadBlogsState.run(
         () =>
            BlogsManagerApiService.getBlogs(null, {
               tokenKey,
               adminID,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.rawBlogs = resp.response.map((b) => ({
                        ...b,
                        created: DateUtilsService.toUnix(b.created),
                     }));
                  });
               }
            },
         }
      );
   }

   async activatePlan({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AdminActivateBlogDto;
      onSuccess?: (res: AdminActivateBlogResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.activateBlogState.run(
         () =>
            BlogsManagerApiService.activateBlog(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadBlogs();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deactivateBlog({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AdminDeactivateBlogDto;
      onSuccess?: (res: AdminDeactivateBlogResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivateBlogState.run(
         () =>
            BlogsManagerApiService.deactivateBlog(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadBlogs();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deleteBlog({
      dto,
      onSuccess,
      onError,
   }: {
      dto: AdminDeleteBlogDto;
      onSuccess?: (res: AdminDeleteBlogResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deleteBlogState.run(
         () =>
            BlogsManagerApiService.deleteBlog(dto, {
               adminID,
               tokenKey,
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.(resp);
                  this.loadBlogs();
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async activateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: AdminBlogIndexResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.activateAllBlogState.run(
         () =>
            BlogsManagerApiService.groupAction(
               {
                  idList: this.idsArray.join(','),
                  action: 'activate',
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.loadBlogs();
                     onSuccess?.(resp);
                  });
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deactivateAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: AdminBlogIndexResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deactivateAllBlogState.run(
         () =>
            BlogsManagerApiService.groupAction(
               {
                  idList: this.idsArray.join(','),
                  action: 'deactivate',
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.loadBlogs();
                     onSuccess?.(resp);
                  });
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }

   async deleteAll({
      onSuccess,
      onError,
   }: {
      onSuccess?: (res: AdminBlogIndexResponse) => void;
      onError?: (e: string) => void;
   }) {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await this.deleteAllBlogState.run(
         () =>
            BlogsManagerApiService.groupAction(
               {
                  idList: this.idsArray.join(','),
                  action: 'delete',
               },
               {
                  adminID,
                  tokenKey,
               }
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.loadBlogs();
                     onSuccess?.(resp);
                  });
               } else {
                  onError?.(resp.message);
               }
            },
            onError: (err) => {
               onError?.(err.message);
            },
         }
      );
   }
}
